import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useSelector } from 'react-redux'
import { CustomButton, Modal, SvgWarning } from '@ui-kit'
import type {
  EcrmSessionRootState,
  IdlePolicyMinutes,
  SessionIdleNamespaces,
} from './types'
import {
  recordSessionActivity,
  recordSessionPromptContinue,
  seedSessionActivityNow,
  setSessionPromptOpen,
} from './sessionInactivity'
import { subscribeSessionActivityBroadcast } from './sessionCrossTab'
import { registerIdleTimerReset } from './sessionIdleTimerBridge'
import { setSessionIdleRemainingMs } from './sessionIdleRemainingStore'
import { useSessionWallClockPauseActive } from './sessionTimerPause'

export interface SessionIdleTimerEngineProps {
  namespaces: SessionIdleNamespaces
  idlePolicy: IdlePolicyMinutes | null
  onIdleLogout: () => void | Promise<void>
  /** When true, idle countdown and logout are suspended (e.g. CTI call). */
  ctiCallActive?: boolean
}

/**
 * Isolated idle timer + modal. Lives as a **sibling** to route children so
 * react-idle-timer internal updates do not re-render the router tree.
 */
export function SessionIdleTimerEngine({
  namespaces,
  idlePolicy,
  onIdleLogout,
  ctiCallActive,
}: SessionIdleTimerEngineProps): ReactElement | null {
  const isAuthenticated = useSelector(
    (s: EcrmSessionRootState) => s.session.isAuthenticated
  )
  const sessionLoginAtMs = useSelector(
    (s: EcrmSessionRootState) => s.session.sessionLoginAtMs ?? 0
  )
  const durablePause = useSessionWallClockPauseActive(sessionLoginAtMs)
  const pauseActive = Boolean(ctiCallActive) || durablePause

  const [showWarning, setShowWarning] = useState(false)
  const logoutStartedRef = useRef(false)
  const idleTimerRef = useRef<ReturnType<typeof useIdleTimer> | null>(null)
  const idleDisplayFrozenMsRef = useRef<number | null>(null)
  const pauseActiveRef = useRef(false)
  pauseActiveRef.current = pauseActive

  const enabled =
    idlePolicy !== null &&
    idlePolicy.timeoutMinutes > 0 &&
    idlePolicy.warningMinutes > 0 &&
    idlePolicy.warningMinutes < idlePolicy.timeoutMinutes

  const timeoutMs = enabled ? idlePolicy.timeoutMinutes * 60 * 1000 : 0
  const warningMs = enabled
    ? Math.min(idlePolicy.warningMinutes * 60 * 1000, timeoutMs - 1000)
    : 0

  useEffect(() => {
    logoutStartedRef.current = false
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !enabled) return
    seedSessionActivityNow()
  }, [isAuthenticated, enabled])

  useEffect(() => {
    if (pauseActive) {
      setShowWarning(false)
      setSessionPromptOpen(false)
    }
  }, [pauseActive])

  useEffect(() => {
    if (!isAuthenticated || !enabled) {
      setSessionPromptOpen(false)
    }
  }, [isAuthenticated, enabled])

  useEffect(() => {
    if (!isAuthenticated || !enabled) return
    return subscribeSessionActivityBroadcast(() => {
      setSessionPromptOpen(false)
      setShowWarning(false)
    })
  }, [isAuthenticated, enabled])

  const onPrompt = useCallback(() => {
    if (pauseActiveRef.current) return
    setSessionPromptOpen(true)
    setShowWarning(true)
  }, [])

  const onIdle = useCallback(() => {
    if (pauseActiveRef.current) return
    if (logoutStartedRef.current) return
    setSessionPromptOpen(false)
    logoutStartedRef.current = true
    void onIdleLogout()
  }, [onIdleLogout])

  const onActive = useCallback(() => {
    setSessionPromptOpen(false)
    setShowWarning(false)
  }, [])

  const onAction = useCallback(() => {
    recordSessionActivity()
  }, [])

  const idleTimer = useIdleTimer({
    timeout: timeoutMs,
    promptBeforeIdle: warningMs,
    onPrompt,
    onIdle,
    onActive,
    onAction,
    crossTab: true,
    syncTimers: 1000,
    leaderElection: false,
    disabled: !isAuthenticated || !enabled || Boolean(ctiCallActive),
    eventsThrottle: 500,
    throttle: 1000,
  })

  idleTimerRef.current = idleTimer

  useLayoutEffect(() => {
    if (!isAuthenticated || !enabled) {
      idleDisplayFrozenMsRef.current = null
      return
    }
    const api = idleTimerRef.current
    if (!api) return
    if (pauseActive) {
      api.pause()
      const t = api.getRemainingTime()
      const ms = typeof t === 'number' ? Math.max(0, t) : null
      idleDisplayFrozenMsRef.current = ms
      if (ms !== null) {
        setSessionIdleRemainingMs(ms)
      }
    } else {
      idleDisplayFrozenMsRef.current = null
      api.resume()
    }
  }, [pauseActive, isAuthenticated, enabled])

  useEffect(() => {
    registerIdleTimerReset(() => idleTimerRef.current?.reset())
    return () => registerIdleTimerReset(null)
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !enabled) {
      setSessionIdleRemainingMs(null)
      return
    }
    const tick = () => {
      if (pauseActive) {
        const frozen = idleDisplayFrozenMsRef.current
        if (typeof frozen === 'number') {
          setSessionIdleRemainingMs(frozen)
        } else {
          setSessionIdleRemainingMs(null)
        }
        return
      }
      const t = idleTimerRef.current?.getRemainingTime()
      setSessionIdleRemainingMs(typeof t === 'number' ? Math.max(0, t) : null)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => {
      clearInterval(id)
    }
  }, [isAuthenticated, enabled, timeoutMs, pauseActive])

  if (!isAuthenticated || !enabled) {
    return null
  }

  if (pauseActive) {
    return null
  }

  return (
    <Modal
      isOpen={showWarning}
      closeModal={() => {
        /* blocking — use Continue only */
      }}
      showCloseButton={false}
      closeModalOnOutsideClick={false}
      allowOutsideClick={false}
      preventBackgroundInteraction
      width="w-full max-w-md"
    >
      <div className="px-12 py-10 flex flex-col items-center text-center border-b border-accent-200">
        <div className="h-12 w-12 flex items-center justify-center rounded-full bg-warning-100 mb-4">
          <SvgWarning height="1.5rem" width="1.5rem" />
        </div>
        <h2 className="text-base font-medium text-accent-800">
          {namespaces.title}
        </h2>
        <p className="mt-3 text-xs text-accent-600 font-light leading-relaxed whitespace-pre-line">
          {namespaces.body}
        </p>
      </div>
      <div className="p-6 flex justify-center">
        <CustomButton
          type="button"
          isFilled
          color="primary"
          onClick={() => {
            recordSessionPromptContinue()
            idleTimer.activate()
            setShowWarning(false)
          }}
          dataQaId="session-inactivity-continue"
        >
          <span className="text-xs">{namespaces.continue}</span>
        </CustomButton>
      </div>
    </Modal>
  )
}
